import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { canMakeAnotherPayment } from 'utilities';
import { FIXED_SIDE } from 'consts';
import {
  getCartItems,
  hideCartItemsSelector,
  getSelectedCurrency,
  isDisplayMerchantCurrencyOnly,
  getCheckoutPageData,
  isDisplayPaymentDescription
} from 'redux/selectors/checkout';

export const usePaymentBreakdown = () => {
  const cartItems = useSelector(getCartItems);
  const hideCartItems = useSelector(hideCartItemsSelector);
  const selectedCurrency = useSelector(getSelectedCurrency);
  const merchantCurrencyOnly = useSelector(isDisplayMerchantCurrencyOnly);
  const checkoutPage = useSelector(getCheckoutPageData);
  const displayPaymentDescription = useSelector(isDisplayPaymentDescription);

  const {
    id: pageToken,
    payment,
    payment_fx_metadata: paymentFxMetadata,
    payments_metadata: paymentsMetadata,
    calculated_payment_fees: calculatedPaymentFees
  } = checkoutPage || {};

  const { id: paymentId, status } = payment || {};

  const canCreateAnotherPayment = canMakeAnotherPayment({
    paymentStatus: status,
    paymentsMetadata
  });

  const paymentWasProcessed = paymentId && !canCreateAnotherPayment;

  const breakdown = useMemo(() => {
    let {
      merchant_requested_currency: requestedCurrency,
      merchant_requested_amount: requestedAmount,
      currency_code: currency,
      amount
    } = paymentFxMetadata || {};
    const { fixed_side: fixedSide } = paymentFxMetadata || {};
    let { fx_fee: fxFeeAmount } = calculatedPaymentFees || {};
    const { transaction_fee: transactionFeeAmount } = calculatedPaymentFees || {};
    let fixedSideBuy = fixedSide === FIXED_SIDE.BUY;
    let fixedSideSell = fixedSide === FIXED_SIDE.SELL;
    let selectedOriginalCurrency = selectedCurrency === requestedCurrency;
    let transactionIncludesFx =
      fixedSideSell || (fixedSideBuy && !selectedOriginalCurrency);

    if (paymentWasProcessed) {
      const {
        fixed_side: paymentFixedSide,
        merchant_requested_currency: paymentRequestedCurrency,
        merchant_requested_amount: paymentRequestedAmount,
        currency_code: paymentCurrencyCode,
        amount: paymentAmount
      } = payment || {};
      requestedCurrency = paymentRequestedCurrency;
      requestedAmount = paymentRequestedAmount;
      currency = paymentCurrencyCode;
      amount = paymentAmount;
      fixedSideBuy = paymentFixedSide === FIXED_SIDE.BUY;
      fixedSideSell = paymentFixedSide === FIXED_SIDE.SELL;
      selectedOriginalCurrency = selectedCurrency === requestedCurrency;
      transactionIncludesFx =
        fixedSideSell || (fixedSideBuy && !selectedOriginalCurrency);
      if (!transactionIncludesFx) {
        fxFeeAmount = 0;
      }
    }
    const cartHasItems = cartItems.length !== 0;
    const hasFees = Boolean(
      transactionFeeAmount || (fxFeeAmount && transactionIncludesFx)
    );
    const showCartItems = cartHasItems && !hideCartItems;
    const enableExpand = Boolean(
      showCartItems || (fixedSideBuy && requestedCurrency) || hasFees
    );

    const discount =
      cartItems.reduce((acc, item) => {
        if (!item.discount) return acc;
        return acc + item.discount * item.quantity;
      }, 0) || 0;

    const originalAmount =
      (fixedSideBuy ? requestedAmount : amount) - fxFeeAmount - transactionFeeAmount;
    const originalCurrency = fixedSideBuy ? requestedCurrency : currency;

    let totalAmount = selectedOriginalCurrency ? requestedAmount - fxFeeAmount : amount;
    let totalCurrency = selectedOriginalCurrency ? requestedCurrency : currency;

    if (paymentWasProcessed) {
      const { amount: paymentAmount, currency_code: paymentCurrency } = payment || {};
      totalAmount = paymentAmount;
      totalCurrency = paymentCurrency;
    }

    const cartProps = {
      hideCartItems,
      cartHasItems,
      showCartItems,
      cartTotalItemsLength: cartItems.reduce(
        (acc, { quantity = 0 }) => acc + Number(quantity),
        0
      )
    };
    const paymentProps = {
      originalAmount,
      originalCurrency,
      totalAmount,
      totalCurrency,
      fixedSideBuy,
      fixedSideSell,
      transactionFeeAmount,
      fxFeeAmount,
      selectedOriginalCurrency,
      merchantCurrencyOnly,
      transactionIncludesFx,
      hasFees,
      subtotalShown: fixedSideBuy && requestedCurrency && !selectedOriginalCurrency,
      showOrderTotalSummary: Boolean(
        fixedSideBuy && requestedCurrency && (transactionFeeAmount || fxFeeAmount)
      ),
      discount,
      originalAmountShown:
        (fixedSideBuy && !selectedOriginalCurrency) || hasFees || !!discount
    };

    const paymentWithAdditionalDetails =
      transactionIncludesFx || displayPaymentDescription || showCartItems || hasFees;

    const result = {
      cartProps,
      paymentProps,
      rest: {
        enableExpand,
        paymentWithAdditionalDetails
      }
    };

    // if display merchant currency only, we should show only the amount (merchant currency) after calculating the fees inside
    if (merchantCurrencyOnly) {
      result.rest = {
        ...result.rest,
        enableExpand: false
      };
      result.paymentProps = {
        ...result.paymentProps,
        totalAmount: requestedAmount,
        totalCurrency: requestedCurrency,
        subtotalShown: false,
        showOrderTotalSummary: false,
        originalAmountShown: false
      };
    }

    return result;
  }, [
    hideCartItems,
    selectedCurrency,
    pageToken,
    JSON.stringify(cartItems),
    JSON.stringify(paymentFxMetadata),
    JSON.stringify(calculatedPaymentFees)
  ]);

  return breakdown;
};
